import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format } from 'date-fns';
import { download, fetchStringContent } from '../../util/download';
import Button from '../button';
import useSetCustomCursor from '../../hooks/useSetCustomCursor';
import { useIntl } from 'react-intl';
import translations from './calendar.i18n';
import { ConfirmationDialog } from '../dialog';
import { formatIsoTzLess, getStudyYearForDate } from '../../date.utils';
import DatePicker from '../datepicker';
import * as css from './calendar.module.scss';
import { NotificationFunctionContext } from '../notifications/notificationProvider';
import dayjs from 'dayjs';

export enum CalendarDownloadApiType {
  PraksisstedApi = 'praksisstedapi',
  StudiestedApi = 'studiestedapi',
}

type CalendarDownloadProps = {
  startYear: number;
  api: CalendarDownloadApiType;
};

const CalendarDownload: React.FC<
  React.PropsWithChildren<CalendarDownloadProps>
> = ({ startYear, api }) => {
  const { formatMessage } = useIntl();
  const { addErrorNotification } = useContext(NotificationFunctionContext);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const btnWrapperRef = useRef<HTMLDivElement>(null);

  const onClose = useCallback(
    (submit: boolean, fromDate: string, toDate: string) => {
      setDialogIsOpen(false);

      if (!submit) return;

      setLoadingCsv(true);

      const now = format(Date.now(), 'ddMMyyyy_HHmmss');

      fetchStringContent(
        `${window.location.origin}/${api}/praksiskalender/download?fromDate=${fromDate}&toDate=${toDate}`,
      )
        .then((fileString) =>
          download({
            filename: `praksiskalender_${fromDate}_${toDate}_${now}.csv`,
            fileType: 'text/csv',
            fileString,
          }),
        )
        .catch((err: Error) => addErrorNotification(err.message))
        .finally(() => setLoadingCsv(false));
    },
    [api, addErrorNotification],
  );

  useSetCustomCursor({
    cursorType: 'wait',
    shouldSetCursor: loadingCsv,
    ref: btnWrapperRef,
  });

  const btnLabel = useMemo(
    () => formatMessage({ id: translations.downloadCsv.id }),
    [formatMessage],
  );

  return (
    <>
      <div ref={btnWrapperRef}>
        <Button.Primary
          disabled={loadingCsv}
          onClick={() => setDialogIsOpen(true)}
        >
          {btnLabel}
        </Button.Primary>
      </div>
      {dialogIsOpen && (
        <DownloadDatePickerDialog onClose={onClose} startYear={startYear} />
      )}
    </>
  );
};

type DownloadDatePickerDialogProps = {
  onClose: (submit: boolean, fromDate: string, toDate: string) => void;
  startYear: number;
};

const DownloadDatePickerDialog: React.FC<
  React.PropsWithChildren<DownloadDatePickerDialogProps>
> = ({ onClose, startYear }) => {
  const initDate = new Date();
  initDate.setFullYear(startYear, 6, 1);

  const [initFromDate, initToDate] = getStudyYearForDate(initDate);
  const [fromDate, setFromDate] = useState(formatIsoTzLess(initFromDate));
  const [toDate, setToDate] = useState(formatIsoTzLess(initToDate));

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      downloadCsvDialogTitle: formatMessage({
        id: translations.downloadCsvDialogTitle.id,
      }),
      downloadCsvDialogSubtitle: formatMessage({
        id: translations.downloadCsvDialogSubtitle.id,
      }),
      downloadCsvFromDate: formatMessage({
        id: translations.downloadCsvFromDate.id,
      }),
      downloadCsvToDate: formatMessage({
        id: translations.downloadCsvToDate.id,
      }),
    }),
    [formatMessage],
  );

  const fromDateObject = useMemo(() => new Date(fromDate), [fromDate]);
  const toDateObject = useMemo(() => new Date(toDate), [toDate]);

  const submitDisabled = useMemo(
    () => fromDateObject > toDateObject,
    [fromDateObject, toDateObject],
  );

  const onCloseHandler = (submit: boolean) => onClose(submit, fromDate, toDate);

  return (
    <ConfirmationDialog
      title={labels.downloadCsvDialogTitle}
      subtitle={labels.downloadCsvDialogSubtitle}
      onClose={onCloseHandler}
      submitDisabled={submitDisabled}
      isOpen
    >
      <DatePicker
        label={labels.downloadCsvFromDate}
        value={fromDate}
        onChange={(date) => setFromDate(date)}
        constraints={{ maxDate: dayjs(toDateObject) }}
        className={css.downloadDialogPickers}
      />
      <DatePicker
        label={labels.downloadCsvToDate}
        value={toDate}
        onChange={(date) => setToDate(date)}
        constraints={{ minDate: dayjs(toDateObject) }}
        className={css.downloadDialogPickers}
      />
    </ConfirmationDialog>
  );
};

export default CalendarDownload;
